import React from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import {
  getWorkHourSettings,
  getWorkHourSetting,
  createWorkHourSetting,
  updateWorkHourSetting,
} from '../../actions/work-hour-settings.action'
import './scss/work-hour-settings.scss'
import moment from 'moment'

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
const DAY_LABELS = {
  monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday',
  thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday',
}
const DAY_SHORT = {
  monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed',
  thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun',
}

// Types used inside time period bands (weekday rules and exception time periods)
const TIME_TYPES = [
  { value: 'STANDARD', key: 'Standard' },
  { value: 'OVERTIME_1', key: 'Overtime' },
  { value: 'PUBLIC_HOLIDAY', key: 'Holiday' },
]

// Categories used to classify an exception day itself
const EXCEPTION_TYPES = [
  { value: 'PUBLIC_HOLIDAY', key: 'Holiday' },
  { value: 'RDO', key: 'RDO' },
  { value: 'CUSTOM', key: 'Custom' },
]


function buildEmptyWeekdayRules() {
  const r = {}
  DAYS.forEach((d) => { r[d] = [] })
  return r
}

export function validateTimeBands(bands) {
  if (!bands || bands.length < 2) return null
  const sorted = [...bands].sort((a, b) => a.start.localeCompare(b.start))
  for (let i = 0; i < sorted.length - 1; i++) {
    if (sorted[i].end > sorted[i + 1].start) {
      return `Overlap: ${sorted[i].start}–${sorted[i].end} overlaps with ${sorted[i + 1].start}–${sorted[i + 1].end}`
    }
  }
  return null
}

function formatTypeLabel(time_type) {
  return TIME_TYPES.find((t) => t.value === time_type)?.key || time_type
}

const BLANK_FORM = {
  name: '', isActive: true,
  selectedDay: 'monday',
  // Weekday time period editing state
  pStart: '', pEnd: '', pType: 'STANDARD', pMultiplier: 1.0,
  pError: '', selectedPeriodIdx: null, showAddPeriod: false,
  // Exception editing state
  eDate: '', eName: '', eExceptionType: 'PUBLIC_HOLIDAY', eTimePeriods: [],
  // Exception time period sub-editing state
  epStart: '', epEnd: '', epType: 'STANDARD', epMultiplier: 1.0,
  epError: '', selectedExcPeriodIdx: null, showAddExcPeriod: false,
  eError: '', selectedExcIdx: null, showAddException: false,
  nameError: '',
}

class WorkHourSettingsPage extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      settings: [],
      selectedId: null,
      weekdayRules: buildEmptyWeekdayRules(),
      exceptions: [],
      saving: false,
      ...BLANK_FORM,
    }
  }

  componentDidMount() {
    this.loadSettings()
  }

  loadSettings = (keepSelection = false) => {
    this.props.getWorkHourSettings().then((result) => {
      const list = Array.isArray(result) ? result : (result?.data || [])
      this.setState({ settings: list }, () => {
        if (keepSelection && this.state.selectedId) {
          this.selectSetting(this.state.selectedId)
        } else if (!keepSelection && !this.state.selectedId && list.length > 0) {
          this.selectSetting(list[0].id)
        }
      })
    }).catch(() => {})
  }

  selectSetting = (id) => {
    this.props.getWorkHourSetting(id).then((result) => {
      const s = result?.data || result
      if (!s) return
      this.populateForm(s)
    }).catch(() => {})
  }

  populateForm = (s) => {
    const weekdayRules = buildEmptyWeekdayRules()
    if (s.weekday_rules) {
      DAYS.forEach((d) => {
        if (Array.isArray(s.weekday_rules[d])) {
          weekdayRules[d] = s.weekday_rules[d].map((b) => ({ ...b }))
        }
      })
    }
    // Support both new format ({ date, name, exception_type, time_periods })
    // and old format ({ name, dates, rules }) for backward compatibility
    const exceptions = []
    if (Array.isArray(s.date_exceptions)) {
      s.date_exceptions.forEach((item) => {
        if (item.date) {
          // New format — individual exception with its own time_periods
          exceptions.push({
            date: item.date,
            name: item.name,
            exception_type: item.exception_type || 'PUBLIC_HOLIDAY',
            time_periods: Array.isArray(item.time_periods) ? item.time_periods : [],
          })
        } else if (item.dates) {
          // Old grouped format — flatten and migrate
          const exception_type = item.rules?.[0]?.time_type || 'PUBLIC_HOLIDAY'
          ;(item.dates || []).forEach((date) => {
            exceptions.push({
              date,
              name: item.name,
              exception_type,
              time_periods: Array.isArray(item.rules) ? item.rules : [],
            })
          })
        }
      })
    }
    this.setState({
      selectedId: s.id,
      weekdayRules,
      exceptions,
      name: s.name || '',
      isActive: s.is_active !== false,
      selectedDay: 'monday',
      pStart: '', pEnd: '', pType: 'STANDARD', pMultiplier: 1.0,
      pError: '', selectedPeriodIdx: null, showAddPeriod: false,
      eDate: '', eName: '', eExceptionType: 'PUBLIC_HOLIDAY', eTimePeriods: [],
      epStart: '', epEnd: '', epType: 'STANDARD', epMultiplier: 1.0,
      epError: '', selectedExcPeriodIdx: null, showAddExcPeriod: false,
      eError: '', selectedExcIdx: null, showAddException: false,
      nameError: '',
    })
  }

  startNew = () => {
    this.setState({
      selectedId: null,
      weekdayRules: buildEmptyWeekdayRules(),
      exceptions: [],
      ...BLANK_FORM,
    })
  }

  // ── Weekday time period helpers ──────────────────────────────────────────────

  selectPeriod = (idx) => {
    const { weekdayRules, selectedDay } = this.state
    const p = weekdayRules[selectedDay][idx]
    this.setState({
      selectedPeriodIdx: idx, showAddPeriod: false,
      pStart: p.start, pEnd: p.end, pType: p.time_type,
      pMultiplier: p.multiplier, pError: '',
    })
  }

  cancelPeriodEdit = () => {
    this.setState({
      selectedPeriodIdx: null, showAddPeriod: false,
      pStart: '', pEnd: '', pType: 'STANDARD', pMultiplier: 1.0, pError: '',
    })
  }

  addOrUpdatePeriod = () => {
    const { weekdayRules, selectedDay, pStart, pEnd, pType, pMultiplier, selectedPeriodIdx } = this.state
    if (!pStart || !pEnd) { this.setState({ pError: 'Start and end time are required' }); return }
    if (pEnd <= pStart) { this.setState({ pError: 'End time must be after start time' }); return }
    const band = { start: pStart, end: pEnd, time_type: pType, multiplier: parseFloat(pMultiplier) || 1.0 }
    const list = [...(weekdayRules[selectedDay] || [])]
    if (selectedPeriodIdx !== null) { list[selectedPeriodIdx] = band } else { list.push(band) }
    const overlapErr = validateTimeBands(list)
    if (overlapErr) { this.setState({ pError: overlapErr }); return }
    this.setState({
      weekdayRules: { ...weekdayRules, [selectedDay]: list },
      pStart: '', pEnd: '', pType: 'STANDARD', pMultiplier: 1.0,
      pError: '', selectedPeriodIdx: null, showAddPeriod: false,
    })
  }

  deletePeriod = (idx) => {
    const { weekdayRules, selectedDay } = this.state
    this.setState({
      weekdayRules: { ...weekdayRules, [selectedDay]: weekdayRules[selectedDay].filter((_, i) => i !== idx) },
      pStart: '', pEnd: '', pType: 'STANDARD', pMultiplier: 1.0,
      pError: '', selectedPeriodIdx: null, showAddPeriod: false,
    })
  }

  // ── Exception helpers ────────────────────────────────────────────────────────

  selectException = (idx) => {
    const e = this.state.exceptions[idx]
    this.setState({
      selectedExcIdx: idx, showAddException: false,
      eDate: e.date, eName: e.name, eExceptionType: e.exception_type || 'PUBLIC_HOLIDAY',
      eTimePeriods: [...(e.time_periods || [])],
      epStart: '', epEnd: '', epType: 'STANDARD', epMultiplier: 1.0,
      epError: '', selectedExcPeriodIdx: null, showAddExcPeriod: false,
      eError: '',
    })
  }

  startAddException = () => {
    this.setState({
      showAddException: true, selectedExcIdx: null,
      eDate: '', eName: '', eExceptionType: 'PUBLIC_HOLIDAY', eTimePeriods: [],
      epStart: '', epEnd: '', epType: 'STANDARD', epMultiplier: 1.0,
      epError: '', selectedExcPeriodIdx: null, showAddExcPeriod: false,
      eError: '',
    })
  }

  cancelExceptionEdit = () => {
    this.setState({
      selectedExcIdx: null, showAddException: false,
      eDate: '', eName: '', eExceptionType: 'PUBLIC_HOLIDAY', eTimePeriods: [],
      epStart: '', epEnd: '', epType: 'STANDARD', epMultiplier: 1.0,
      epError: '', selectedExcPeriodIdx: null, showAddExcPeriod: false,
      eError: '',
    })
  }

  addOrUpdateException = () => {
    const { exceptions, eDate, eName, eExceptionType, eTimePeriods, selectedExcIdx } = this.state
    if (!eDate || !eName) { this.setState({ eError: 'Date and name are required' }); return }
    const formatted = moment(eDate).isValid() ? moment(eDate).format('YYYY-MM-DD') : eDate
    const list = [...exceptions]
    const entry = { date: formatted, name: eName, exception_type: eExceptionType, time_periods: [...eTimePeriods] }
    if (selectedExcIdx !== null) {
      list[selectedExcIdx] = entry
    } else {
      if (list.some((e) => e.date === formatted)) { this.setState({ eError: 'This date is already added' }); return }
      list.push(entry)
    }
    this.setState({
      exceptions: list,
      eDate: '', eName: '', eExceptionType: 'PUBLIC_HOLIDAY', eTimePeriods: [],
      epStart: '', epEnd: '', epType: 'STANDARD', epMultiplier: 1.0,
      epError: '', selectedExcPeriodIdx: null, showAddExcPeriod: false,
      eError: '', selectedExcIdx: null, showAddException: false,
    })
  }

  deleteException = (idx) => {
    this.setState({
      exceptions: this.state.exceptions.filter((_, i) => i !== idx),
      eDate: '', eName: '', eExceptionType: 'PUBLIC_HOLIDAY', eTimePeriods: [],
      epStart: '', epEnd: '', epType: 'STANDARD', epMultiplier: 1.0,
      epError: '', selectedExcPeriodIdx: null, showAddExcPeriod: false,
      eError: '', selectedExcIdx: null, showAddException: false,
    })
  }

  // ── Exception time period sub-helpers ────────────────────────────────────────

  selectExcPeriod = (idx) => {
    const p = this.state.eTimePeriods[idx]
    this.setState({
      selectedExcPeriodIdx: idx, showAddExcPeriod: false,
      epStart: p.start, epEnd: p.end, epType: p.time_type, epMultiplier: p.multiplier, epError: '',
    })
  }

  cancelExcPeriodEdit = () => {
    this.setState({
      selectedExcPeriodIdx: null, showAddExcPeriod: false,
      epStart: '', epEnd: '', epType: 'STANDARD', epMultiplier: 1.0, epError: '',
    })
  }

  addOrUpdateExcPeriod = () => {
    const { eTimePeriods, epStart, epEnd, epType, epMultiplier, selectedExcPeriodIdx } = this.state
    if (!epStart || !epEnd) { this.setState({ epError: 'Start and end time are required' }); return }
    if (epEnd <= epStart) { this.setState({ epError: 'End time must be after start time' }); return }
    const band = { start: epStart, end: epEnd, time_type: epType, multiplier: parseFloat(epMultiplier) || 1.0 }
    const list = [...eTimePeriods]
    if (selectedExcPeriodIdx !== null) { list[selectedExcPeriodIdx] = band } else { list.push(band) }
    const overlapErr = validateTimeBands(list)
    if (overlapErr) { this.setState({ epError: overlapErr }); return }
    this.setState({
      eTimePeriods: list,
      epStart: '', epEnd: '', epType: 'STANDARD', epMultiplier: 1.0,
      epError: '', selectedExcPeriodIdx: null, showAddExcPeriod: false,
    })
  }

  deleteExcPeriod = (idx) => {
    this.setState({
      eTimePeriods: this.state.eTimePeriods.filter((_, i) => i !== idx),
      epStart: '', epEnd: '', epType: 'STANDARD', epMultiplier: 1.0,
      epError: '', selectedExcPeriodIdx: null, showAddExcPeriod: false,
    })
  }

  // ── Save ─────────────────────────────────────────────────────────────────────

  buildExceptionPayload = () => {
    return this.state.exceptions.map(({ date, name, exception_type, time_periods }) => ({
      date,
      name,
      exception_type: exception_type || 'PUBLIC_HOLIDAY',
      time_periods: time_periods || [],
    }))
  }

  save = () => {
    const { name, isActive, weekdayRules, selectedId } = this.state
    if (!name.trim()) { this.setState({ nameError: 'Name is required' }); return }
    const payload = {
      name: name.trim(),
      is_active: isActive,
      weekday_rules: weekdayRules,
      date_exceptions: this.buildExceptionPayload(),
    }
    this.setState({ saving: true })
    const action = selectedId
      ? this.props.updateWorkHourSetting(selectedId, payload)
      : this.props.createWorkHourSetting(payload)
    action.then(() => {
      this.setState({ saving: false })
      this.loadSettings(!!selectedId)
    }).catch(() => { this.setState({ saving: false }) })
  }

  cancel = () => {
    if (this.state.selectedId) { this.selectSetting(this.state.selectedId) } else { this.startNew() }
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  render() {
    const {
      settings, selectedId, saving, nameError,
      name, isActive,
      selectedDay, weekdayRules, exceptions,
      pStart, pEnd, pType, pMultiplier, pError, selectedPeriodIdx, showAddPeriod,
      eDate, eName, eExceptionType, eTimePeriods, eError, selectedExcIdx, showAddException,
      epStart, epEnd, epType, epMultiplier, epError, selectedExcPeriodIdx, showAddExcPeriod,
    } = this.state
    const { writePermission } = this.props
    const currentPeriods = weekdayRules[selectedDay] || []
    const periodColSpan = writePermission ? 5 : 4
    const excColSpan = writePermission ? 5 : 4
    const excEditOpen = showAddException || selectedExcIdx !== null

    const periodInputRow = (isNew) => (
      <tr className="whs-edit-row">
        <td>
          <input type="time" className="form-control whs-inline-input" value={pStart}
            onChange={(e) => this.setState({ pStart: e.target.value, pError: '' })}
            disabled={!writePermission} data-cy="period-start" />
        </td>
        <td>
          <input type="time" className="form-control whs-inline-input" value={pEnd}
            onChange={(e) => this.setState({ pEnd: e.target.value, pError: '' })}
            disabled={!writePermission} data-cy="period-end" />
        </td>
        <td>
          <Form.Select className="form-control whs-inline-input" value={pType}
            onChange={(e) => this.setState({ pType: e.target.value })}
            disabled={!writePermission} data-cy="period-type">
            {TIME_TYPES.map((t) => <option key={t.value} value={t.value}>{t.key}</option>)}
          </Form.Select>
        </td>
        <td>
          <input type="number" step="0.25" min="0" className="form-control whs-inline-input" value={pMultiplier}
            onChange={(e) => this.setState({ pMultiplier: e.target.value, pError: '' })}
            disabled={!writePermission} data-cy="period-multiplier" />
        </td>
        {writePermission && (
          <td className="whs-action-cell">
            <Button className="secondarybtn btn-sm me-2" onClick={this.addOrUpdatePeriod} data-cy="add-period-btn">
              {isNew ? 'Add' : 'Update'}
            </Button>
            <button className="btn btn-sm secondarybtn cancelbtn" onClick={this.cancelPeriodEdit}>Cancel</button>
          </td>
        )}
      </tr>
    )

    const excPeriodInputRow = (isNew) => (
      <tr className="whs-edit-row">
        <td>
          <input type="time" className="form-control whs-inline-input" value={epStart}
            onChange={(e) => this.setState({ epStart: e.target.value, epError: '' })}
            disabled={!writePermission} data-cy="exc-period-start" />
        </td>
        <td>
          <input type="time" className="form-control whs-inline-input" value={epEnd}
            onChange={(e) => this.setState({ epEnd: e.target.value, epError: '' })}
            disabled={!writePermission} data-cy="exc-period-end" />
        </td>
        <td>
          <Form.Select className="form-control whs-inline-input" value={epType}
            onChange={(e) => this.setState({ epType: e.target.value })}
            disabled={!writePermission} data-cy="exc-period-type">
            {TIME_TYPES.map((t) => <option key={t.value} value={t.value}>{t.key}</option>)}
          </Form.Select>
        </td>
        <td>
          <input type="number" step="0.25" min="0" className="form-control whs-inline-input" value={epMultiplier}
            onChange={(e) => this.setState({ epMultiplier: e.target.value, epError: '' })}
            disabled={!writePermission} data-cy="exc-period-multiplier" />
        </td>
        {writePermission && (
          <td className="whs-action-cell">
            <Button className="secondarybtn btn-sm me-2" onClick={this.addOrUpdateExcPeriod} data-cy="add-exc-period-btn">
              {isNew ? 'Add' : 'Update'}
            </Button>
            <button className="btn btn-sm secondarybtn cancelbtn" onClick={this.cancelExcPeriodEdit}>Cancel</button>
          </td>
        )}
      </tr>
    )

    return (
      <div className="whs-page">
        {/* ── Left panel ─────────────────────────────────────────────────── */}
        <div className="whs-list-panel">
          <div className="whs-list-header">Available Settings</div>
          <div className="whs-list-body">
            {settings.length === 0 && <p className="whs-empty-hint">No settings yet</p>}
            {settings.map((s) => (
              <div
                key={s.id}
                className={`whs-list-item${s.id === selectedId ? ' selected' : ''}${!s.is_active ? ' inactive' : ''}`}
                onClick={() => this.selectSetting(s.id)}
                data-cy={`setting-item-${s.id}`}
              >
                <span className="whs-list-name">{s.name}</span>
                {!s.is_active && <span className="whs-inactive-badge">Inactive</span>}
              </div>
            ))}
          </div>
          {writePermission && (
            <div className="whs-list-footer">
              <button className="btn btn-sm btn-outline-primary w-100" onClick={this.startNew} data-cy="new-setting-btn">
                + New Setting
              </button>
            </div>
          )}
        </div>

        {/* ── Right panel ────────────────────────────────────────────────── */}
        <div className="whs-form-panel">

          {/* Name + Active */}
          <div className="whs-info-card mb-4">
            <div className="row g-3 align-items-center">
              <div className="col">
                <label className="input-label">Name <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className={`form-control form-control-input${nameError ? ' is-invalid' : ''}`}
                  value={name}
                  onChange={(e) => this.setState({ name: e.target.value, nameError: '' })}
                  disabled={!writePermission}
                  data-cy="setting-name-input"
                />
                {nameError && <small className="text-danger">{nameError}</small>}
              </div>
              <div className="col-auto">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input switch-checkbox"
                    type="checkbox"
                    id="active-switch"
                    checked={isActive}
                    onChange={(e) => this.setState({ isActive: e.target.checked })}
                    disabled={!writePermission}
                    data-cy="active-checkbox"
                  />
                  <label className="form-check-label" htmlFor="active-switch">Active</label>
                </div>
              </div>
            </div>
          </div>

          {/* Time Periods */}
          <div className="whs-section mb-4">
            <div className="whs-section-header">
              <span className="whs-section-label">Time Periods</span>
              <div className="whs-day-tabs" data-cy="day-tabs">
                {DAYS.map((d) => {
                  const count = (weekdayRules[d] || []).length
                  return (
                    <button
                      key={d}
                      className={`whs-day-tab${selectedDay === d ? ' active' : ''}${count > 0 ? ' has-periods' : ''}`}
                      onClick={() => this.setState({
                        selectedDay: d, selectedPeriodIdx: null, showAddPeriod: false,
                        pStart: '', pEnd: '', pType: 'STANDARD', pMultiplier: 1.0, pError: '',
                      })}
                      data-cy={`day-tab-${d}`}
                      title={count > 0 ? `${count} period${count > 1 ? 's' : ''} configured` : `No periods for ${DAY_LABELS[d]}`}
                    >
                      {DAY_SHORT[d]}
                      {count > 0 && <span className="whs-day-count">{count}</span>}
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="whs-table-wrap" data-cy="time-periods-list">
              <table className="whs-table">
                <thead>
                  <tr>
                    <th style={{ width: '15%' }}>Start</th>
                    <th style={{ width: '15%' }}>End</th>
                    <th>Type</th>
                    <th style={{ width: '13%' }}>Multiplier</th>
                    {writePermission && <th style={{ width: '110px' }} />}
                  </tr>
                </thead>
                <tbody>
                  {currentPeriods.length === 0 && !showAddPeriod && (
                    <tr>
                      <td colSpan={periodColSpan} className="whs-empty-cell">
                        No time periods configured for {DAY_LABELS[selectedDay]}
                      </td>
                    </tr>
                  )}
                  {currentPeriods.map((p, i) =>
                    selectedPeriodIdx === i
                      ? <React.Fragment key={i}>{periodInputRow(false)}</React.Fragment>
                      : (
                        <tr key={i} className="whs-data-row" data-cy={`period-row-${i}`}>
                          <td>{p.start}</td>
                          <td>{p.end}</td>
                          <td>{formatTypeLabel(p.time_type)}</td>
                          <td>{p.multiplier}×</td>
                          {writePermission && (
                            <td className="whs-action-cell">
                              <ModeEditOutlineOutlinedIcon className="edit-icon me-2" onClick={() => this.selectPeriod(i)} />
                              <DeleteOutlineOutlinedIcon className="delete-icon" onClick={() => this.deletePeriod(i)} data-cy="delete-period-btn" />
                            </td>
                          )}
                        </tr>
                      )
                  )}
                  {showAddPeriod && periodInputRow(true)}
                </tbody>
              </table>
              {pError && <small className="text-danger d-block mt-2">{pError}</small>}
              {writePermission && !showAddPeriod && selectedPeriodIdx === null && (
                <button
                  className="btn btn-sm btn-outline-primary mt-3"
                  onClick={() => this.setState({
                    showAddPeriod: true, selectedPeriodIdx: null,
                    pStart: '', pEnd: '', pType: 'STANDARD', pMultiplier: 1.0, pError: '',
                  })}
                >
                  + Add Period
                </button>
              )}
            </div>
          </div>

          {/* Exception Days */}
          <div className="whs-section mb-4">
            <div className="whs-section-header">
              <span className="whs-section-label">Exception Days</span>
            </div>

            <div className="whs-table-wrap" data-cy="exceptions-list">
              <table className="whs-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Time Periods</th>
                    {writePermission && <th />}
                  </tr>
                </thead>
                <tbody>
                  {exceptions.length === 0 && (
                    <tr>
                      <td colSpan={excColSpan} className="whs-empty-cell">No exception days added</td>
                    </tr>
                  )}
                  {exceptions.map((e, i) => (
                    <tr
                      key={i}
                      className={`whs-data-row${selectedExcIdx === i ? ' whs-row-selected' : ''}`}
                      data-cy={`exception-row-${i}`}
                    >
                      <td>{e.date}</td>
                      <td>{e.name}</td>
                      <td>{EXCEPTION_TYPES.find((t) => t.value === e.exception_type)?.key || e.exception_type}</td>
                      <td>
                        <span className="whs-periods-badge">
                          {(e.time_periods || []).length} period{(e.time_periods || []).length !== 1 ? 's' : ''}
                        </span>
                      </td>
                      {writePermission && (
                        <td className="whs-action-cell">
                          <ModeEditOutlineOutlinedIcon className="edit-icon me-2" onClick={() => this.selectException(i)} />
                          <DeleteOutlineOutlinedIcon className="delete-icon" onClick={() => this.deleteException(i)} data-cy="delete-exception-btn" />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Exception add/edit panel */}
            {excEditOpen && (
              <div className="whs-exc-edit-panel">
                <div className="whs-exc-edit-title">
                  {selectedExcIdx !== null ? 'Edit Exception' : 'New Exception'}
                </div>

                <div className="row g-3 mb-3">
                  <div className="col-md-3">
                    <label className="whs-field-label">Date <span className="text-danger">*</span></label>
                    <input type="date" className="form-control form-control-input" value={eDate}
                      onChange={(e) => this.setState({ eDate: e.target.value, eError: '' })}
                      disabled={!writePermission} data-cy="exception-date" />
                  </div>
                  <div className="col-md-5">
                    <label className="whs-field-label">Name <span className="text-danger">*</span></label>
                    <input type="text" className="form-control form-control-input" value={eName}
                      onChange={(e) => this.setState({ eName: e.target.value, eError: '' })}
                      disabled={!writePermission} data-cy="exception-name" placeholder="e.g. Christmas Day" />
                  </div>
                  <div className="col-md-4">
                    <label className="whs-field-label">Exception Type</label>
                    <Form.Select className="form-control form-control-input" value={eExceptionType}
                      onChange={(e) => this.setState({ eExceptionType: e.target.value })}
                      disabled={!writePermission} data-cy="exception-type">
                      {EXCEPTION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.key}</option>)}
                    </Form.Select>
                  </div>
                </div>

                {/* Time periods for this exception */}
                <div className="whs-exc-periods-section">
                  <div className="whs-exc-periods-label">Time Periods</div>
                  <div className="whs-table-wrap">
                    <table className="whs-table">
                      <thead>
                        <tr>
                          <th style={{ width: '15%' }}>Start</th>
                          <th style={{ width: '15%' }}>End</th>
                          <th>Type</th>
                          <th style={{ width: '13%' }}>Multiplier</th>
                          {writePermission && <th style={{ width: '110px' }} />}
                        </tr>
                      </thead>
                      <tbody>
                        {eTimePeriods.length === 0 && !showAddExcPeriod && (
                          <tr>
                            <td colSpan={writePermission ? 5 : 4} className="whs-empty-cell">
                              No time periods — add one below
                            </td>
                          </tr>
                        )}
                        {eTimePeriods.map((p, i) =>
                          selectedExcPeriodIdx === i
                            ? <React.Fragment key={i}>{excPeriodInputRow(false)}</React.Fragment>
                            : (
                              <tr key={i} className="whs-data-row" data-cy={`exc-period-row-${i}`}>
                                <td>{p.start}</td>
                                <td>{p.end}</td>
                                <td>{formatTypeLabel(p.time_type)}</td>
                                <td>{p.multiplier}×</td>
                                {writePermission && (
                                  <td className="whs-action-cell">
                                    <ModeEditOutlineOutlinedIcon className="edit-icon me-2" onClick={() => this.selectExcPeriod(i)} />
                                    <DeleteOutlineOutlinedIcon className="delete-icon" onClick={() => this.deleteExcPeriod(i)} />
                                  </td>
                                )}
                              </tr>
                            )
                        )}
                        {showAddExcPeriod && excPeriodInputRow(true)}
                      </tbody>
                    </table>
                  </div>
                  {epError && <small className="text-danger d-block mt-2">{epError}</small>}
                  {writePermission && !showAddExcPeriod && selectedExcPeriodIdx === null && (
                    <button
                      className="btn btn-sm btn-outline-primary mt-2"
                      onClick={() => this.setState({
                        showAddExcPeriod: true, selectedExcPeriodIdx: null,
                        epStart: '', epEnd: '', epType: 'STANDARD', epMultiplier: 1.0, epError: '',
                      })}
                    >
                      + Add Time Period
                    </button>
                  )}
                </div>

                {eError && <small className="text-danger d-block mt-3">{eError}</small>}
                <div className="whs-exc-edit-actions">
                  <Button className="secondarybtn btn-sm me-2" onClick={this.addOrUpdateException} data-cy="add-exception-btn">
                    {selectedExcIdx !== null ? 'Update Exception' : 'Add Exception'}
                  </Button>
                  <button className="btn btn-sm secondarybtn cancelbtn" onClick={this.cancelExceptionEdit}>Cancel</button>
                </div>
              </div>
            )}

            {writePermission && !excEditOpen && (
              <button
                className="btn btn-sm btn-outline-primary mt-3"
                onClick={this.startAddException}
              >
                + Add Exception
              </button>
            )}
          </div>

          {/* Footer */}
          {writePermission && (
            <div className="whs-form-footer">
              <Button className="secondarybtn cancelbtn" onClick={this.cancel} data-cy="cancel-btn">Cancel</Button>
              <Button className="secondarybtn" onClick={this.save} disabled={saving} data-cy="save-btn">
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          )}
        </div>
      </div>
    )
  }
}

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ getWorkHourSettings, getWorkHourSetting, createWorkHourSetting, updateWorkHourSetting }, dispatch)

export default connect(null, mapDispatchToProps)(WorkHourSettingsPage)
